/**
 * Network presets for `@takeover/sdk` consumers.
 *
 * These presets bundle every constant a builder needs to wire the
 * SDK against a live takeover.fun deployment: the EVM chain id +
 * RPC, the public API service base, the indexer GraphQL endpoint,
 * and the canonical v2.3 UGM + library + yield-adapter addresses.
 *
 * Source of truth:
 *   - The published presets here ARE the source of truth for the
 *     `/builders/contracts` page and the docs `reference/deployments.md`
 *     table. App-level env files mirror these values; the SDK preset
 *     wins when the two ever drift.
 *   - Test networks intentionally have *known* RPCs (`base.org`,
 *     `sepolia.base.org`) so a builder can `import { sepolia }` and
 *     immediately spin up a `<TakeoverGrid />` without wrangling
 *     Alchemy or Infura keys. Production builds should still pass
 *     `rpcUrl: process.env.NEXT_PUBLIC_RPC_URL` to bypass the public
 *     RPC's rate limits.
 *
 * Usage:
 *   ```ts
 *   import { createTakeoverSDK, base } from '@takeover/sdk';
 *
 *   const sdk = createTakeoverSDK({
 *     ...base.toConfig({ apiKey: process.env.TAKEOVER_API_KEY! }),
 *     grids: [{ ugmAddress: base.ugmV23, gridId: '1' }],
 *   });
 *   ```
 */

import type { TakeoverSDKConfig } from '../types';

export interface NetworkYieldAdapters {
  /** Flaunch v2.3 yield adapter. Optional — may be unset until
   *  `54_DeployYieldAdaptersV22.s.sol` has been re-broadcast against
   *  the v2.3 UGM on this network. */
  flaunch?: `0x${string}`;
  /** Uniswap V3 LP-NFT yield adapter (v2.3-bound). */
  v3?: `0x${string}`;
  /** Uniswap V4 LP-NFT yield adapter (v2.3-bound). */
  v4?: `0x${string}`;
}

export interface NetworkPreset {
  /** Human-readable network name (mirrors viem chain naming). */
  name: string;
  /** EVM chain id. */
  chainId: number;
  /** Best-effort default RPC. Builders should override with a paid
   *  RPC for production; the public ones rate-limit aggressively. */
  defaultRpcUrl: string;
  /** Public API service base. The SDK appends `/v1/...` paths. */
  apiBaseUrl: string;
  /** Indexer GraphQL endpoint (Envio HyperIndex). */
  indexerUrl: string;
  /** Block explorer root for deeplinks. */
  explorerUrl: string;
  /** Canonical USDC address used as the tax token. */
  usdcAddress: `0x${string}`;
  /** Latest UGM v2.3 deployment. The only manager that exposes
   *  `moduleTransferSeat` and the extended `IGridHooksV23` callbacks. */
  ugmV23: `0x${string}`;
  /** External `UGMV23Linked` library (DELEGATECALL target).
   *  Same on every network the UGM is deployed on. */
  library: `0x${string}`;
  /** Yield-adapter trio bound to the v2.3 UGM. Members are optional
   *  per network — `Sepolia` has no v2.3 adapter trio yet. */
  yieldAdapters: NetworkYieldAdapters;
  /** Guardian / contract-owner that runs `setApprovedAdapter`,
   *  `setApprovedGovernanceModule`, `setApprovedModule`, and the
   *  pause flag operations. */
  guardian: `0x${string}`;
  /**
   * Helper that fills in the no-runtime-state fields of a
   * `TakeoverSDKConfig`. Builders pass their grid list (and an
   * optional override RPC) and the SDK does the rest.
   */
  toConfig(input: {
    grids: TakeoverSDKConfig['grids'];
    rpcUrl?: string;
    privyAppId?: string;
    chatBackendUrl?: string;
  }): TakeoverSDKConfig;
}

function presetFromCore(
  core: Omit<NetworkPreset, 'toConfig'>,
): NetworkPreset {
  return {
    ...core,
    toConfig({ grids, rpcUrl, privyAppId, chatBackendUrl }) {
      return {
        apiBaseUrl: core.apiBaseUrl,
        chainId: core.chainId,
        rpcUrl: rpcUrl ?? core.defaultRpcUrl,
        grids,
        privyAppId,
        chatBackendUrl: chatBackendUrl ?? core.apiBaseUrl,
      };
    },
  };
}

/**
 * Base mainnet — the production deployment. UGM v2.3 is live with
 * the full yield-adapter trio (Flaunch / V3 / V4) approved on the
 * v2.3 UGM's `approvedAdapters` allowlist.
 *
 * Deploy provenance:
 *   - script: `takeover-contracts/script/56_DeployUGMv23.s.sol`
 *   - salt:    keccak256("takeover-ugm-V23-V3")  // May 2026 refactor redeploy
 *   - basescan https://basescan.org/address/0xF2DfBe1ef26AA82e9438dA95d5cC3007D3031F9e
 *
 * The refactor packs `forfeitedFrom` + `forfeitedByTax` into
 * `forfeitInfo`, merges `_claimForSeat` + `_accumulateFees` into
 * `_claimSeatYield`, extracts `_abandonSeatInternal`, centralizes
 * constants in `UGMV23Constants`, drops the ASCII banner, adds a
 * `gridConfigV23` alias, renames `pendingFees(uint, address)` to
 * `pendingFeesForHolder`, and promotes `creatorBaseline` to
 * public. Storage layout breaks vs the previous salt -V2 deploy
 * (`0xac02F47a…3F3A`); that deploy held no grids and is abandoned.
 */
export const base = presetFromCore({
  name: 'Base',
  chainId: 8453,
  defaultRpcUrl: 'https://mainnet.base.org',
  apiBaseUrl: 'https://api.takeover.fun',
  indexerUrl: 'https://indexer.takeover.fun/v1/graphql',
  explorerUrl: 'https://basescan.org',
  usdcAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  ugmV23: '0xF2DfBe1ef26AA82e9438dA95d5cC3007D3031F9e',
  library: '0x2b4530d49ccCBdF22005d84C175F552334a61445',
  yieldAdapters: {
    flaunch: '0x1867650664c252186a214D5ed37CB1FD736b7bE6',
    v3: '0x0205dA82768Ecd4C6E66f94e5A97F63B8dE1EbCf',
    v4: '0x06e31B4EfCCdb8450a2e29C590A4AF81843d3172',
  },
  guardian: '0x6b9Db1337B37426a7911e4108d27D44393B95eec',
});

/**
 * Base Sepolia — the public test network. UGM v2.3 + library + the
 * full yield-adapter trio are now address-aligned with mainnet
 * (May 2026 refactor redeploy at salt -V3 on the UGM, salt -V8 on
 * the adapters). Sepolia and mainnet share the same guardian
 * (`0x6b9Db1337B…5eec`) so a single `GUARDIAN_PRIVATE_KEY` wires
 * both networks.
 *
 * Deploy provenance:
 *   - script: `takeover-contracts/script/56_DeployUGMv23.s.sol`
 *   - salt:    keccak256("takeover-ugm-V23-V3")
 *   - basescan https://sepolia.basescan.org/address/0xC389D001C627C7fcE23405190d453906598c1607
 *
 * Prior salt -V2 deploy (`0xf67159948…fD10C`, library
 * `0x989cfDE8…6eCb1`, guardian `0x0646441c…BEfb6e`) had
 * `nextGridId == 0` and is abandoned.
 */
export const sepolia = presetFromCore({
  name: 'Base Sepolia',
  chainId: 84532,
  defaultRpcUrl: 'https://sepolia.base.org',
  apiBaseUrl: 'https://takeover-api-staging.up.railway.app',
  indexerUrl: 'https://indexer-staging.takeover.fun/v1/graphql',
  explorerUrl: 'https://sepolia.basescan.org',
  usdcAddress: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
  ugmV23: '0xC389D001C627C7fcE23405190d453906598c1607',
  library: '0xb92EbB9fEbcf09F534B75bD312a66e1C8c918562',
  yieldAdapters: {
    flaunch: '0x444f97f0db32fa12e3c444b9f83b4b51f2767f59',
    v3: '0x0f8387cd6ecad2618a3255baf8ec84a803b2efdb',
    v4: '0xAd97b449277de911d740026087547c582AA1FB59',
  },
  guardian: '0x6b9Db1337B37426a7911e4108d27D44393B95eec',
});

/** Lookup table for `getNetwork(chainId)` consumers. */
export const NETWORKS: Record<number, NetworkPreset> = {
  [base.chainId]: base,
  [sepolia.chainId]: sepolia,
};

/** Resolve a preset by chain id; returns `undefined` for unknown
 *  networks so consumers can short-circuit before composing config. */
export function getNetwork(chainId: number): NetworkPreset | undefined {
  return NETWORKS[chainId];
}
